import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { Box, CircularProgress } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import ReactGA from 'react-ga4';
import { updateCurrency } from '~/actions';
import { AppState, Currency, RootState } from '~/types';
import { CURRENCY, SELECTED_CURRENCY_STORAGE_KEY } from '~/literals';
import { hasAcceptedTrackingConsent } from '~/components/GDPRConsentModal';

type Props = {
  wlId?: string;
  width?: number | string;
  height?: number | string;
  onSearchClick?: () => void;
};

const DEFAULT_WL_ID = '17957';
const MOBILE_SEARCH_SCROLL_MAX_WIDTH = 600;

const TripPayoutsWidget: React.FC<Props> = ({ wlId = DEFAULT_WL_ID, width = '100%', height = 'auto', onSearchClick }) => {
  const location = useLocation();
  const dispatch = useDispatch();
  const app = useSelector<RootState, AppState>(({ app }) => app);
  const { currency } = app;
  const lastSyncedCurrencyRef = useRef<Currency | null>(null);

  useEffect(() => {
    const scriptId = `tpwl-script-${wlId}`;
    const searchButtonSelector = 'button[data-testid="default-search-submit-button"]';
    const currencyOptionSelector = '[data-testid^="localization-list-option-"]';

    document.querySelectorAll<HTMLScriptElement>(`script[id^="tpwl-script-"]`).forEach((script) => {
      script.remove();
    });

    const script = document.createElement('script');
    script.id = scriptId;
    script.async = true;
    script.type = 'module';
    script.src = `https://tpembd.com/wl_web/main.js?wl_id=${encodeURIComponent(wlId)}`;
    script.setAttribute('data-noptimize', '1');
    script.setAttribute('data-cfasync', 'false');
    script.setAttribute('data-wpfc-render', 'false');
    script.setAttribute('seraph-accel-crit', '1');
    script.setAttribute('data-no-defer', '1');
    document.head.appendChild(script);

    const handleClick = (event: MouseEvent) => {
      const path = event.composedPath ? event.composedPath() : [];

      // Track search button click
      const button = path.find(
        (node) => node instanceof HTMLElement && node.matches(searchButtonSelector)
      ) as HTMLElement | undefined;

      if (button) {
        const isMobileScreen = window.matchMedia(`(max-width: ${MOBILE_SEARCH_SCROLL_MAX_WIDTH}px)`).matches;

        if (isMobileScreen) {
          window.scrollTo({ top: 0, behavior: 'smooth' });
        }

        if (hasAcceptedTrackingConsent()) {
          ReactGA.event({
            category: 'TripPayoutsWidget',
            action: 'Trip Payouts Widget Flight Search Button Clicked',
            label: `wlId:${wlId}`,
          });
        }
        onSearchClick?.();
      }

      // Sync currency selection from widget to Redux
      const currencyOption = path.find(
        (node) => node instanceof HTMLElement && node.matches(currencyOptionSelector)
      ) as HTMLElement | undefined;

      if (currencyOption) {
        const testId = currencyOption.getAttribute('data-testid');
        const currencyCode = testId?.replace('localization-list-option-', '');
        if (currencyCode && Object.values(CURRENCY).includes(currencyCode as Currency)) {
          dispatch(updateCurrency(currencyCode as Currency));
          lastSyncedCurrencyRef.current = currencyCode as Currency;
        }
      }
    };

    // Update widget currency when Redux state changes
    const syncCurrencyToWidget = (targetCurrency: Currency) => {
      if (!targetCurrency) return;

      const tpwlContainer = document.getElementById('tpwl-search') as any;
      if (!tpwlContainer?.shadowRoot) return;

      const currencyLabel = tpwlContainer.shadowRoot.querySelector(
        `label[data-testid="localization-list-option-${targetCurrency}"]`
      ) as HTMLElement | null;

      if (!currencyLabel) return;

      const isAlreadyActive = currencyLabel.classList.contains('LocalizationList-module__active___DihQ-');

      if (!isAlreadyActive) {
        // Click the checkbox inside the label to trigger the widget's internal change handler
        const checkbox = currencyLabel.querySelector('input[type="checkbox"]') as HTMLInputElement;
        if (checkbox && !checkbox.checked) {
          checkbox.checked = true;
          const changeEvent = new Event('change', { bubbles: true, cancelable: true, composed: true });
          checkbox.dispatchEvent(changeEvent);
        }

        // Also dispatch click event as fallback
        const clickEvent = new MouseEvent('click', {
          bubbles: true,
          cancelable: true,
          composed: true,
        });
        currencyLabel.dispatchEvent(clickEvent);
      }
    };

    // Listen for storage changes (currency changes from CurrencySelector)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === SELECTED_CURRENCY_STORAGE_KEY && e.newValue) {
        const newCurrency = e.newValue as Currency;
        if (lastSyncedCurrencyRef.current !== newCurrency) {
          // Delay to ensure widget is ready
          setTimeout(() => {
            syncCurrencyToWidget(newCurrency);
            lastSyncedCurrencyRef.current = newCurrency;
          }, 300);
        }
      }
    };

    // Listen for custom currency change events from CurrencySelector
    const handleCustomCurrencyChange = (event: Event) => {
      const customEvent = event as CustomEvent;
      const newCurrency = customEvent.detail?.currency as Currency;
      if (newCurrency && lastSyncedCurrencyRef.current !== newCurrency) {
        // Small delay to ensure widget is ready
        setTimeout(() => {
          syncCurrencyToWidget(newCurrency);
          lastSyncedCurrencyRef.current = newCurrency;
        }, 300);
      }
    };

    // Call sync after widget loads
    const syncTimer = setTimeout(() => {
      syncCurrencyToWidget(currency);
      lastSyncedCurrencyRef.current = currency;
    }, 1500);

    // Add event listeners
    document.addEventListener('click', handleClick, true);
    window.addEventListener('storage', handleStorageChange);
    window.addEventListener('currencyChanged', handleCustomCurrencyChange);

    return () => {
      clearTimeout(syncTimer);
      document.removeEventListener('click', handleClick, true);
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('currencyChanged', handleCustomCurrencyChange);
      const s = document.getElementById(scriptId);
      if (s && s.parentNode) s.parentNode.removeChild(s);
    };
  }, [wlId, location.pathname, dispatch, onSearchClick]);

  return (
    <Box style={{ width, height }}>
      <Box id="tpwl-search">
        <Box display="flex" alignItems="center" justifyContent="center" width="100%" pt={4} pb={4}>
          <CircularProgress />
        </Box>
      </Box>
    </Box>
  );
};

export default TripPayoutsWidget;
